// F-WEB-017 /admin/usuarios — CRUD usuarios + acesso individual a canais + reset senha.
// Redesign 2026-09-22 (W2): DataTable (tabela desktop / cartoes <48rem),
// avatar de iniciais, email em linha propria, chips de papel/status/canais.
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Key, Plus, ShieldAlert } from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import type { AdminUser, ChannelSummary, Role, RoleOption } from "../../lib/types";
import { PageHeader } from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PasswordField } from "../../components/ui/PasswordField";
import { Label } from "../../components/ui/label";
import { Select } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import { Skeleton } from "../../components/ui/skeleton";
import { EmptyState } from "../../components/ui/empty-state";
import { Card, CardContent } from "../../components/ui/card";
import { DataTable, type DataTableColumn } from "../../components/ui/DataTable";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from "../../components/ui/dialog";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate, roleLabel } from "../../lib/utils";

/**
 * Fallback SE `GET /api/roles` ainda nao existir (API sem a migracao 0024 —
 * RBAC configuravel, plano 2026-09-23). Reproduz a lista fixa de hoje: so
 * quem ja e super_admin pode atribuir super_admin; admin/usuario sempre
 * disponiveis. `has_channels_all` replica o gating atual (superOnly/admin
 * enxergam todos os canais; usuario so os liberados).
 */
function legacyRoleOptions(actorIsSuperAdmin: boolean): RoleOption[] {
  const opts: RoleOption[] = [
    { slug: "usuario", name: "Usuário (acesso limitado a canais)", has_channels_all: false },
    { slug: "admin", name: "Admin (acesso total)", has_channels_all: true },
  ];
  if (actorIsSuperAdmin) {
    opts.push({ slug: "super_admin", name: "Super admin (acesso total + configurações)", has_channels_all: true });
  }
  return opts;
}

/** Avatar rosa com 2 iniciais (fidelidade 3: protótipo usa cor cheia +
 * nome/sobrenome, não a letra única neutra do Swatch). Token --color-pink
 * já existe em index.css; aplicado aqui via style pois este componente não
 * pode tocar o stylesheet central (fora do escopo da frente F4). */
function Avatar({ name }: { name: string }): JSX.Element {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const initials =
    parts.length === 0
      ? "?"
      : parts.length === 1
        ? (parts[0] ?? "?").slice(0, 2).toUpperCase()
        : ((parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")).toUpperCase();
  return (
    <span
      style={{
        width: "2rem",
        height: "2rem",
        borderRadius: "50%",
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "var(--text-sm)",
        fontWeight: 800,
        color: "#fff",
        background: "var(--color-pink)",
      }}
      aria-hidden
    >
      {initials}
    </span>
  );
}

export function AdminUsersPage(): JSX.Element {
  const { user: me } = useAuth();
  const isSuperAdminActor = me?.role === "super_admin";
  const qc = useQueryClient();

  const usersQ = useQuery<AdminUser[]>({
    queryKey: ["admin-users"],
    queryFn: async () => (await api.get<AdminUser[]>("/api/admin/users")).data,
  });
  const channelsQ = useQuery<ChannelSummary[]>({
    queryKey: ["channels"],
    queryFn: async () => (await api.get<ChannelSummary[]>("/api/channels")).data,
  });
  // GET /api/roles (contrato do plano secao 7.3): perfis que o ATOR logado
  // pode atribuir, ja com a flag has_channels_all. API antiga (sem RBAC
  // configuravel) -> 404 -> cai na lista fixa de hoje.
  const rolesQ = useQuery<RoleOption[]>({
    queryKey: ["assignable-roles"],
    queryFn: async () => (await api.get<RoleOption[]>("/api/roles")).data,
    retry: false,
  });
  const roleOptions: RoleOption[] = rolesQ.data ?? legacyRoleOptions(isSuperAdminActor);
  const hasChannelsAllFor = (role: Role): boolean =>
    roleOptions.find((r) => r.slug === role)?.has_channels_all ?? (role === "admin" || role === "super_admin");

  const [createOpen, setCreateOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [tempPwd, setTempPwd] = useState<{ user: string; pwd: string; isNew: boolean } | null>(null);

  const resetMut = useMutation({
    mutationFn: async (id: number) => {
      const r = await api.post<{ temporary_password: string }>(
        `/api/admin/users/${id}/password-reset`,
        {},
      );
      return r.data.temporary_password;
    },
    onSuccess: (pwd, id) => {
      const u = usersQ.data?.find((x) => x.id === id);
      setTempPwd({ user: u?.email ?? `id=${id}`, pwd, isNew: false });
      toast.success("Senha temporária gerada");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  const onReset = (u: AdminUser) => {
    if (confirm(`Resetar senha de ${u.email}?`)) resetMut.mutate(u.id);
  };

  const roleChip = (u: AdminUser) => {
    const label = u.role_name ?? roleLabel(u.role);
    if (u.role === "super_admin") return <span className="chip chip--coral mono">{label}</span>;
    if (u.role === "admin") return <span className="chip chip--lilac mono">{label}</span>;
    return <span className="chip mono">{label}</span>;
  };

  const channelsCell = (u: AdminUser) =>
    hasChannelsAllFor(u.role) ? (
      <span style={{ color: "var(--color-ink-2)" }}>(todos)</span>
    ) : u.allowed_channels.length === 0 ? (
      <span style={{ color: "var(--color-ink-2)" }}>(nenhum)</span>
    ) : (
      <span className="cluster">
        {u.allowed_channels.map((c) => (
          <span key={c} className="chip">{c}</span>
        ))}
      </span>
    );

  const actionsCell = (u: AdminUser) => (
    <span className="cluster" style={{ justifyContent: "flex-end", gap: ".375rem" }}>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditingUser(u)}
        disabled={u.role === "super_admin" && !isSuperAdminActor}
        title={u.role === "super_admin" && !isSuperAdminActor ? "Apenas super_admin edita super_admin" : "Editar"}
      >
        Editar
      </Button>
      <span aria-hidden style={{ color: "var(--color-ink-2)" }}>·</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onReset(u)}
        disabled={resetMut.isPending}
        title="Resetar senha (gera temp)"
        aria-label={`Resetar senha de ${u.email}`}
      >
        Senha
      </Button>
    </span>
  );

  const columns: DataTableColumn<AdminUser>[] = [
    {
      key: "name",
      label: "Nome / e-mail",
      width: "minmax(13rem,3fr)",
      render: (u) => (
        <span className="cluster">
          <Avatar name={u.name} />
          <span className="stack" style={{ gap: 0 }}>
            <span className="row__title truncate">{u.name}</span>
            <span className="truncate" style={{ color: "var(--color-ink-2)", fontSize: "var(--text-sm)" }}>
              {u.email}
            </span>
            {u.must_change_pwd ? <span className="chip chip--warn">trocar senha</span> : null}
          </span>
        </span>
      ),
    },
    { key: "role", label: "Papel", width: "minmax(7rem,1fr)", render: (u) => roleChip(u) },
    { key: "channels", label: "Canais", width: "minmax(8rem,2fr)", render: channelsCell },
    {
      key: "status",
      label: "Status",
      width: "minmax(5rem,1fr)",
      render: (u) => (u.active ? <span className="chip chip--good">ativo</span> : <span className="chip chip--bad">inativo</span>),
    },
    {
      key: "last_login",
      label: "Último login",
      width: "minmax(10.5rem,1fr)",
      mono: true,
      render: (u) => formatDate(u.last_login_at),
    },
    {
      key: "actions",
      label: "",
      width: "minmax(9rem,auto)",
      render: actionsCell,
    },
  ];

  return (
    <div>
      <PageHeader
        title="Usuários"
        description="Gerenciar usuários do sistema e o acesso individual de cada um aos canais."
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="icon" aria-hidden /> Novo usuário
          </Button>
        }
      />

      {usersQ.isLoading ? (
        <Card>
          <CardContent className="stack">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      ) : usersQ.isError ? (
        <EmptyState title="Erro ao carregar usuários" description={apiErrorMessage(usersQ.error)} />
      ) : (
        <DataTable
          columns={columns}
          rows={usersQ.data ?? []}
          rowKey={(u) => u.id}
          emptyState={<EmptyState title="Nenhum usuário" />}
          renderCard={(u) => (
            <div className="row-card">
              <Avatar name={u.name} />
              <div className="row-card__body">
                <span className="cluster">
                  <span className="row__title truncate">{u.name}</span>
                  {u.must_change_pwd ? <span className="chip chip--warn">trocar senha</span> : null}
                </span>
                <span style={{ color: "var(--color-ink-2)" }}>{u.email}</span>
                <div className="cluster">
                  {roleChip(u)}
                  {u.active ? <span className="chip chip--good">ativo</span> : <span className="chip chip--bad">inativo</span>}
                  {channelsCell(u)}
                </div>
                <div className="cluster">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setEditingUser(u)}
                    disabled={u.role === "super_admin" && !isSuperAdminActor}
                    title={u.role === "super_admin" && !isSuperAdminActor ? "Apenas super_admin edita super_admin" : "Editar"}
                  >
                    Editar
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onReset(u)}
                    disabled={resetMut.isPending}
                  >
                    <Key className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden /> Senha
                  </Button>
                </div>
              </div>
            </div>
          )}
        />
      )}

      <CreateUserDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        channels={channelsQ.data ?? []}
        roleOptions={roleOptions}
        onCreated={(email, pwd) => setTempPwd({ user: email, pwd, isNew: true })}
      />
      {editingUser ? (
        <EditUserDialog
          user={editingUser}
          channels={channelsQ.data ?? []}
          meId={me?.id ?? null}
          roleOptions={roleOptions}
          onOpenChange={(o) => {
            if (!o) setEditingUser(null);
          }}
        />
      ) : null}
      {tempPwd ? (
        <Dialog open onOpenChange={(o) => !o && setTempPwd(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {tempPwd.isNew ? "Usuário criado" : "Senha temporária gerada"}
              </DialogTitle>
              <DialogDescription>
                Copie a senha abaixo e envie ao usuário por um canal seguro (WhatsApp, por
                exemplo). No primeiro login ele será obrigado a trocar por uma senha própria.
              </DialogDescription>
            </DialogHeader>
            <div style={{ borderRadius: "var(--radius-md)", border: ".0625rem solid var(--color-warn)", background: "var(--color-warn-soft)", padding: ".75rem" }}>
              <div className="chip" style={{ background: "transparent", padding: 0 }}>Usuário</div>
              <div className="row__title">{tempPwd.user}</div>
              <div className="chip" style={{ background: "transparent", padding: 0, marginTop: ".5rem" }}>Senha inicial</div>
              <code className="mono" style={{ display: "block", marginTop: ".25rem", wordBreak: "break-all", fontSize: "var(--text-md)" }}>
                {tempPwd.pwd}
              </code>
            </div>
            <DialogFooter>
              <DialogClose asChild>
                <Button variant="secondary">Fechar</Button>
              </DialogClose>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(tempPwd.pwd).then(
                    () => toast.success("Senha copiada"),
                    () => toast.error("Falha ao copiar"),
                  );
                }}
              >
                Copiar senha
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

const ROLE_HINT: Record<Role, string> = {
  usuario: "Acessa só os canais liberados abaixo. Uso indicado para operadores.",
  admin: "Acessa todos os canais e as áreas administrativas (menos configurações avançadas).",
  super_admin: "Acesso total, incluindo configurações avançadas do sistema.",
};

function generateStrongPassword(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let out = "";
  for (let i = 0; i < 12; i++) {
    out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
}

function CreateUserDialog({
  open,
  onOpenChange,
  channels,
  roleOptions,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  channels: ChannelSummary[];
  roleOptions: RoleOption[];
  onCreated: (email: string, password: string) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>(roleOptions[0]?.slug ?? "usuario");
  const [password, setPassword] = useState("");
  const [mustChange, setMustChange] = useState(true);
  const [selected, setSelected] = useState<string[]>([]);
  const [touchedChannels, setTouchedChannels] = useState(false);

  const roleHasChannelsAll = roleOptions.find((r) => r.slug === role)?.has_channels_all ?? false;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const passwordValid = password.length >= 8;
  const needsChannel = !roleHasChannelsAll;
  const channelMissing = needsChannel && selected.length === 0;
  const canSubmit = emailValid && name.trim().length > 0 && passwordValid && !channelMissing;

  const mut = useMutation({
    mutationFn: async () => {
      await api.post("/api/admin/users", {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
        password,
        must_change_pwd: mustChange,
        allowed_channels: needsChannel ? selected : undefined,
      });
    },
    onSuccess: () => {
      toast.success("Usuário criado");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      onCreated(email.trim().toLowerCase(), password);
      onOpenChange(false);
      setEmail("");
      setName("");
      setRole(roleOptions[0]?.slug ?? "usuario");
      setPassword("");
      setMustChange(true);
      setSelected([]);
      setTouchedChannels(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Novo usuário</DialogTitle>
          <DialogDescription>Crie um usuário do sistema e defina os canais que ele acessa.</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setTouchedChannels(true);
            if (!canSubmit) return;
            mut.mutate();
          }}
          className="stack"
        >
          <div className="field">
            <Label htmlFor="cu-email">Email</Label>
            <Input
              id="cu-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="off"
              placeholder="nome@hiitfitwear.com"
            />
            {email.length > 0 && !emailValid ? (
              <p className="field__hint" style={{ color: "var(--color-bad)" }}>Digite um email válido.</p>
            ) : null}
          </div>
          <div className="field">
            <Label htmlFor="cu-name">Nome</Label>
            <Input id="cu-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <Label htmlFor="cu-role">Papel no sistema</Label>
            <Select id="cu-role" value={role} onChange={(e) => setRole(e.target.value)}>
              {roleOptions.map((r) => (
                <option key={r.slug} value={r.slug}>{r.name}</option>
              ))}
            </Select>
            <p className="field__hint">{ROLE_HINT[role] ?? "Acesso definido pelas capacidades deste perfil (veja Perfis e acessos)."}</p>
          </div>
          <div className="field">
            <div className="spread">
              <Label htmlFor="cu-pwd">Senha inicial (mínimo 8 caracteres)</Label>
              <button
                type="button"
                className="btn btn--ghost"
                style={{ minHeight: "auto", padding: 0 }}
                onClick={() => setPassword(generateStrongPassword())}
              >
                Gerar senha
              </button>
            </div>
            {/* Regra da casa: todo campo de senha nasce oculto com botao de ver
                (feedback_campo_de_senha_tem_botao_de_ver). */}
            <PasswordField
              id="cu-pwd"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              minLength={8}
              required
              autoComplete="new-password"
              className="mono"
            />
            {password.length > 0 && !passwordValid ? (
              <p className="field__hint" style={{ color: "var(--color-bad)" }}>A senha precisa ter pelo menos 8 caracteres.</p>
            ) : null}
          </div>
          <Switch
            checked={mustChange}
            onChange={(e) => setMustChange(e.currentTarget.checked)}
            label="Usuário troca a senha no primeiro login (recomendado)"
          />
          {needsChannel ? (
            <div className="field">
              <Label>Canais permitidos</Label>
              <div className="stack" style={{ maxHeight: "10rem", overflowY: "auto", border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", padding: ".5rem" }}>
                {channels.length === 0 ? (
                  <div className="field__hint">Nenhum canal disponível</div>
                ) : (
                  channels.map((c) => (
                    <label key={c.slug} className="cluster" style={{ fontSize: "var(--text-md)", fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={selected.includes(c.slug)}
                        onChange={(e) => {
                          setSelected((cur) =>
                            e.target.checked ? [...cur, c.slug] : cur.filter((s) => s !== c.slug),
                          );
                        }}
                      />
                      {c.name}
                      <span className="field__hint">({c.slug})</span>
                    </label>
                  ))
                )}
              </div>
              {touchedChannels && channelMissing ? (
                <p className="field__hint" style={{ color: "var(--color-bad)" }}>
                  Selecione pelo menos um canal, ou ele não vai conseguir acessar nada.
                </p>
              ) : (
                <p className="field__hint">
                  Ele só vai ver e editar produtos dos canais marcados aqui.
                </p>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" type="button">Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={mut.isPending}>Criar usuário</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({
  user,
  channels,
  meId,
  roleOptions,
  onOpenChange,
}: {
  user: AdminUser;
  channels: ChannelSummary[];
  meId: number | null;
  roleOptions: RoleOption[];
  onOpenChange: (o: boolean) => void;
}): JSX.Element {
  const qc = useQueryClient();
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<Role>(user.role);
  const [active, setActive] = useState(user.active);
  const [selected, setSelected] = useState<string[]>(user.allowed_channels);

  const isSelf = meId === user.id;
  // O proprio perfil-alvo (user.role) pode nao estar entre os assignable
  // pelo ator (ex.: editar um super_admin sendo admin — o botao "Editar" ja
  // vem desabilitado nesse caso, mas defensivo aqui tambem); garante que a
  // opcao atual sempre aparece no select mesmo que o ator nao possa
  // REATRIBUIR esse papel a outra pessoa.
  const selectOptions =
    roleOptions.some((r) => r.slug === user.role)
      ? roleOptions
      : [{ slug: user.role, name: user.role_name ?? user.role, has_channels_all: true }, ...roleOptions];
  const roleHasChannelsAll = roleOptions.find((r) => r.slug === role)?.has_channels_all ?? false;
  const needsChannel = !roleHasChannelsAll;

  const patchMut = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      if (name !== user.name) body.name = name;
      if (role !== user.role) body.role = role;
      if (active !== user.active) body.active = active;
      if (Object.keys(body).length > 0) {
        await api.patch(`/api/admin/users/${user.id}`, body);
      }
      // perfil ficou sem channels.all => envia canais individuais; perfis
      // com channels.all ignoram o campo (backend nao usa allowed_channels
      // pra eles, RN da secao 7.4 do plano).
      if (needsChannel) {
        const before = new Set(user.allowed_channels);
        const after = new Set(selected);
        const changed = before.size !== after.size || ![...before].every((s) => after.has(s));
        if (changed) {
          await api.put(`/api/admin/users/${user.id}/channels`, { allowed_channels: selected });
        }
      }
    },
    onSuccess: () => {
      toast.success("Usuário atualizado");
      qc.invalidateQueries({ queryKey: ["admin-users"] });
      onOpenChange(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err)),
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar usuário</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            patchMut.mutate();
          }}
          className="stack"
        >
          <div className="field">
            <Label htmlFor="eu-name">Nome</Label>
            <Input id="eu-name" value={name} onChange={(e) => setName(e.target.value)} required />
          </div>
          <div className="field">
            <Label htmlFor="eu-role">Papel no sistema</Label>
            <Select
              id="eu-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={isSelf}
            >
              {selectOptions.map((r) => (
                <option key={r.slug} value={r.slug}>{r.name}</option>
              ))}
            </Select>
            <p className="field__hint">
              {isSelf
                ? "Você não pode alterar o próprio perfil."
                : (ROLE_HINT[role] ?? "Acesso definido pelas capacidades deste perfil (veja Perfis e acessos).")}
            </p>
          </div>
          <Switch
            checked={active}
            onChange={(e) => setActive(e.currentTarget.checked)}
            disabled={isSelf}
            label={isSelf ? "Ativo (não pode desativar a si próprio)" : "Ativo"}
          />
          {needsChannel ? (
            <div className="field">
              <Label>Canais permitidos</Label>
              {channels.length === 0 ? (
                <div className="field__hint">Nenhum canal disponível</div>
              ) : (
                <div className="stack" style={{ maxHeight: "10rem", overflowY: "auto", border: ".0625rem solid var(--color-line)", borderRadius: "var(--radius-md)", padding: ".5rem" }}>
                  {channels.map((c) => (
                    <label key={c.slug} className="cluster" style={{ fontSize: "var(--text-md)", fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={selected.includes(c.slug)}
                        onChange={(e) => {
                          setSelected((cur) =>
                            e.target.checked ? [...cur, c.slug] : cur.filter((s) => s !== c.slug),
                          );
                        }}
                      />
                      {c.name}
                      <span className="field__hint">({c.slug})</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="cluster field__hint">
              <ShieldAlert className="icon" style={{ width: "1rem", height: "1rem" }} aria-hidden />
              admin e super_admin têm acesso a todos canais ativos.
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="secondary" type="button">Cancelar</Button>
            </DialogClose>
            <Button type="submit" loading={patchMut.isPending}>Salvar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
